<template>
  <div class="sf-home">
    <!-- eyebrow + título + seletor de corretora + refresh agora vivem no navbar -->

    <!-- 1. Faixa de mercado (TradingView) -->
    <div
      v-if="windowWidth >= 900"
      class="sf-market mb-3"
    >
      <TradingViewWidgets :options="{ theme: isThemeDark ? 'dark' : 'light' }" />
    </div>

    <termo-de-uso />

    <!-- 2. Valor estimado + Crédito da taxa -->
    <div class="sf-grid sf-grid-main mb-3">
      <!-- Valor estimado + alocação -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('spot.textValorEstimado') }} · {{ currentExchangeConfig.name }}</span>
        </div>

        <template v-if="loadingOverlayCarteira && errorLoadCarteira == null">
          <b-skeleton
            width="180px"
            height="34px"
            class="mb-3"
            animation="wave"
          />
          <b-skeleton
            width="100%"
            height="210px"
            animation="wave"
          />
        </template>

        <template v-else-if="errorLoadCarteira == null">
          <div class="sf-value mb-1">
            <span class="sf-value-cur">$</span>
            <span class="sf-value-amt sf-num">{{ estimativaUSD }}</span>
          </div>
          <div class="sf-alloc">
            <div class="sf-alloc-chart">
              <vue-apex-charts
                type="donut"
                height="200"
                :options="carteiraGrafico"
                :series="seriesCarteira"
              />
            </div>
            <div class="sf-alloc-legend">
              <div
                v-for="row in carteiraLegenda"
                :key="row.label"
                class="sf-legend-row"
              >
                <span
                  class="sf-swatch"
                  :style="{ background: row.color }"
                />
                <span class="sf-legend-name">{{ row.label }}</span>
                <span class="sf-legend-val sf-num">${{ row.usd.toFixed(2) }}</span>
                <span class="sf-legend-pct sf-num">{{ row.pct.toFixed(0) }}%</span>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="sf-errorstate">
            <feather-icon
              icon="AlertTriangleIcon"
              size="40"
            />
            <h4>{{ $t('spot.errorMessages.unableToLoadPortfolio') }}</h4>
            <p>{{ errorLoadCarteira }}</p>
            <b-button
              variant="primary"
              @click="atualizarDados()"
            >
              {{ $t('spot.errorMessages.tryAgain') }}
            </b-button>
          </div>
        </template>
      </div>

      <!-- Crédito da taxa -->
      <div
        v-if="creditoDesempenho || loadingOverlayCarteira"
        class="sf-card sf-pad sf-credit"
      >
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('spot.textCreditoTaxa') }}</span>
        </div>
        <template v-if="loadingOverlayCarteira && errorLoadCarteira == null">
          <b-skeleton
            width="120px"
            height="30px"
            animation="wave"
          />
        </template>
        <template v-else-if="creditoDesempenho">
          <div class="sf-value mb-1">
            <span class="sf-value-cur">$</span>
            <span class="sf-value-amt sf-num">{{ toFixed(creditoDesempenho.saldo, 2) }}</span>
          </div>
          <div class="sf-credit-sub">
            {{ $t('spot.textCreditoTaxaSub') }}
          </div>
          <div class="sf-credit-actions">
            <router-link
              class="sf-btn sf-btn-primary"
              :to="{ name: 'checkout-credito' }"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
              />
              {{ $t('spot.textAdicionarSaldo') }}
            </router-link>
            <router-link
              class="sf-btn sf-btn-secondary"
              :to="{ name: 'conta-credito-extrato' }"
            >
              <feather-icon
                icon="FileTextIcon"
                size="16"
              />
              {{ $t('spot.textExtrato') }}
            </router-link>
          </div>
        </template>
      </div>
    </div>

    <!-- 3. Saldo de ativos + Assertividade do mês -->
    <div class="sf-grid sf-grid-main mb-3">
      <!-- Saldo de ativos -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('spot.assetBalances.title') }} · {{ currentExchangeConfig.name }}</span>
        </div>
        <div
          v-if="loadingOverlayCarteira"
          class="sf-assets"
        >
          <div
            v-for="n in 4"
            :key="`as-sk-${n}`"
            class="sf-asset"
          >
            <div class="sf-asset-info w-100">
              <b-skeleton
                width="60px"
                height="14px"
                class="mb-1"
                animation="wave"
              />
              <b-skeleton
                width="110px"
                height="20px"
                class="mb-1"
                animation="wave"
              />
              <b-skeleton
                width="80px"
                height="12px"
                animation="wave"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="sf-assets"
        >
          <div class="sf-asset">
            <div
              class="sf-asset-tile"
              style="color:#26A17B"
            >
              USDT
            </div>
            <div class="sf-asset-info">
              <div class="sf-asset-total sf-num">
                {{ parseFloat(totalUSDT).toFixed(6) }}
              </div>
              <div class="sf-asset-free">
                {{ $t('spot.assetBalances.available') }}: <span class="sf-num">{{ parseFloat(livreUSDT).toFixed(6) }}</span>
              </div>
            </div>
          </div>
          <div class="sf-asset">
            <div
              class="sf-asset-tile"
              style="color:#2775CA"
            >
              USDC
            </div>
            <div class="sf-asset-info">
              <div class="sf-asset-total sf-num">
                {{ parseFloat(totalUSDC).toFixed(6) }}
              </div>
              <div class="sf-asset-free">
                {{ $t('spot.assetBalances.available') }}: <span class="sf-num">{{ parseFloat(livreUSDC).toFixed(6) }}</span>
              </div>
            </div>
          </div>
          <div class="sf-asset">
            <div
              class="sf-asset-tile"
              style="color:#F0B90B"
            >
              BTC
            </div>
            <div class="sf-asset-info">
              <div class="sf-asset-total sf-num">
                {{ parseFloat(totalBTC).toFixed(6) }}
              </div>
              <div class="sf-asset-free">
                {{ $t('spot.assetBalances.available') }}: <span class="sf-num">{{ parseFloat(livreBTC).toFixed(6) }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="isBinanceActive"
            class="sf-asset"
          >
            <div
              class="sf-asset-tile"
              style="color:#F3BA2F"
            >
              BNB
            </div>
            <div class="sf-asset-info">
              <div class="sf-asset-total sf-num">
                {{ parseFloat(totalBNB).toFixed(6) }}
              </div>
              <div class="sf-asset-free">
                {{ $t('spot.assetBalances.available') }}: <span class="sf-num">{{ parseFloat(livreBNB).toFixed(6) }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="isMexcActive"
            class="sf-asset"
          >
            <div
              class="sf-asset-tile"
              style="color:#1652F0"
            >
              MX
            </div>
            <div class="sf-asset-info">
              <div class="sf-asset-total sf-num">
                {{ parseFloat(totalMX).toFixed(6) }}
              </div>
              <div class="sf-asset-free">
                {{ $t('spot.assetBalances.available') }}: <span class="sf-num">{{ parseFloat(livreMX).toFixed(6) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assertividade do mês -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('spot.titleCardAssertividadeMensal') }} · {{ currentExchangeConfig.name }}</span>
        </div>
        <div
          v-if="loadingOverlayCarteira"
          class="text-center"
        >
          <b-skeleton
            width="100%"
            height="200px"
            animation="wave"
          />
        </div>
        <div
          v-else
          class="sf-assert"
        >
          <div class="sf-assert-chart">
            <grafico-sinal
              :resumo-sinal="resumoSinal"
              :series="seriesSinal"
              :hide-labels="true"
              :title="$t('spot.performance.signals')"
            />
          </div>
          <div class="sf-assert-stats">
            <div class="sf-stat">
              <span
                class="sf-swatch"
                style="background:var(--sf-fg-3)"
              />
              <span class="sf-stat-label">{{ $t('spot.assetBalances.active') }}</span>
              <span class="sf-stat-val sf-num">{{ resumoSinal.totalAtivo || 0 }}</span>
            </div>
            <div class="sf-stat">
              <span
                class="sf-swatch"
                style="background:var(--sf-green)"
              />
              <span class="sf-stat-label">{{ $t('spot.assetBalances.wins') }}</span>
              <span class="sf-stat-val sf-num sf-up">{{ resumoSinal.totalGanho || 0 }}</span>
            </div>
            <div class="sf-stat">
              <span
                class="sf-swatch"
                style="background:var(--sf-red)"
              />
              <span class="sf-stat-label">{{ $t('spot.assetBalances.losses') }}</span>
              <span class="sf-stat-val sf-num sf-down">{{ resumoSinal.totalPerda || 0 }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Vendas recentes -->
    <div class="sf-card sf-table-card">
      <div class="sf-table-title">
        {{ $t('spot.recentSales') }}
      </div>
      <div class="sf-scroll">
        <template v-if="loadingOverlayCarteira">
          <b-skeleton-table
            :rows="6"
            :columns="tableColumns.length"
            animation="wave"
            class="p-2"
          />
        </template>
        <b-table
          v-else
          ref="refListTable"
          :items="dataListUltimasVendas"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('futures.emptyText')"
          class="sf-table"
        >
          <template #cell(corretora)="data">
            <div class="sf-cell-exchange">
              <img
                :src="require(`@/assets/images/logo/${data.value.toLowerCase()}-logo.png`)"
                :alt="data.value"
                class="sf-cell-exchange-logo"
              >
              <span>{{ data.value }}</span>
            </div>
          </template>

          <template #cell(paridade)="data">
            <div class="sf-cell-pair">
              <b-avatar
                size="sm"
                :src="getIcon(data.item.moedaTrade)"
              />
              <span class="sf-pair-base">{{ data.item.moedaTrade }}</span>
              <span class="sf-pair-quote">/{{ data.item.paridadeTrade }}</span>
            </div>
          </template>

          <template #cell(pnl)="data">
            <div class="sf-cell-pnl">
              <span :class="parseFloat(data.value) >= 0 ? 'sf-up' : 'sf-down'">
                <span class="sf-num">{{ toFixed(data.item.pnl, 4) }} {{ data.item.paridadeTrade }}</span>
                <small class="sf-num">({{ getPercentDiff(data.item.totalCompra, data.item.total) }}%)</small>
              </span>
            </div>
          </template>

          <template #cell(duracao)="data">
            <span class="sf-num sf-muted">{{ duracao(data.item.dataCriacaoCompra, data.item.dataExecucao) }}</span>
          </template>

          <template #cell(dataCriacaoCompra)="data">
            <div class="sf-cell-date">
              <span class="sf-num">{{ formatDateBR(data.value) }}</span>
              <span class="sf-num sf-muted">{{ formatHourBR(data.value) }}</span>
            </div>
          </template>

          <template #cell(agente)="data">
            <div class="sf-cell-agent">
              <span>{{ data.value }}</span>
              <span class="sf-signal-pill sf-num">#{{ data.item.sinalId }}</span>
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
 import {
   BCard, BLink, BTable, BBadge, BAvatar, BRow, BCol, BCardTitle, BOverlay, BCardHeader, BCardText, BCardBody, VBTooltip, BButton, BMedia, BMediaAside, BMediaBody, BSkeleton, BSkeletonTable, BSpinner,
 } from 'bootstrap-vue'
import TradingViewWidgets from '@/layouts/components/custom/TradingViewWidgets.vue'
import VueApexCharts from 'vue-apexcharts'
import store from '@/store'
import utilsMixin from '@/mixins/utilsMixin'
import {
  formatHourBR, formatDateBR, formatDateTimeSemAnoBR, percentDiferenca, duracao, toIsoStringTimeZone,
} from '@/utils/filter'
import fechamentoStoreModule from './relatorio/fechamento/fechamentoStoreModule'
import sinalStoreModule from './relatorio/sinal/sinalStoreModule'
import usuarioStoreModule from './usuario/usuarioStoreModule'
import BoasVindasPopup from './usuario/BoasVindasPopup.vue'
import TermoDeUso from '../termos/TermoDeUso.vue'

const RELATORIO_FECHAMENTO_SPOT_STORE_MODULE_NAME = 'fechamento-relatorio-spot'
const SINAL_SPOT_STORE_MODULE_NAME = 'sinal-relatorio-spot'
const USUARIO_SPOT_STORE_MODULE_NAME = 'usuario-spot'

export default {
  components: {
    TradingViewWidgets,
    BCard,
    BLink,
    BTable,
    BBadge,
    BAvatar,
    BRow,
    BCol,
    BCardTitle,
    VueApexCharts,
    BOverlay,
    BoasVindasPopup,
    TermoDeUso,
    BCardHeader,
    BCardText,
    BCardBody,
    BButton,
    BMedia,
    BMediaAside,
    BMediaBody,
    BSkeleton,
    BSkeletonTable,
    BSpinner,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  mixins: [utilsMixin],

  data() {
    return {
      errorLoadCarteira: null,
      loadingOverlayCarteira: false,
      loadingOverlaySinal: false,
      estimativaUSD: '',
      estimativaBTC: '',
      formatHourBR,
      formatDateBR,
      formatDateTimeSemAnoBR,
      percentDiferenca,
      duracao,
      dataListUltimasVendas: [],
      resumoSinal: [],
      seriesSinal: [],
      totalBtcCarteira: '',
      totalUSDTCarteira: '',
      atualizacaoCarteira: '',
      dataAtual: new Date(),
      seriesCarteira: [],
      labelsCarteira: [],
      totalBTC: 0,
      totalUSDT: 0,
      totalUSDC: 0,
      totalBNB: 0,
      totalMX: 0,
      livreUSDT: 0,
      livreUSDC: 0,
      livreBNB: 0,
      livreMX: 0,
      livreBTC: 0,
      creditoDesempenho: null,
      selectedExchange: null,
      exchangeConfig: {
        binance: {
          name: 'Binance',
          variant: 'light-warning',
          cardClass: 'binance-theme',
          color: '#F0B90B',
        },
        mexc: {
          name: 'MEXC',
          variant: 'light-primary',
          cardClass: 'mexc-theme',
          color: '#005CE6',
        },
      },
      // reserved for future interactions
      _lastPillFocusTs: 0,
    }
  },

  computed: {
    userData() {
      return JSON.parse(localStorage.getItem('userData') || '{}')
    },

    corretorasAtivas() {
      return this.userData.corretorasAtivas || []
    },

    availableExchanges() {
      const exchanges = {}
      this.corretorasAtivas.forEach(corretora => {
        const exchangeKey = corretora.toLowerCase()
        if (this.exchangeConfig[exchangeKey]) {
          exchanges[exchangeKey] = this.exchangeConfig[exchangeKey]
        }
      })
      return exchanges
    },

    isBinanceActive() {
      return this.corretorasAtivas.includes('BINANCE')
    },

    isMexcActive() {
      return this.corretorasAtivas.includes('MEXC')
    },

    firstActiveExchange() {
      const keys = Object.keys(this.availableExchanges)
      return keys.length > 0 ? keys[0] : 'binance'
    },

    tableColumns() {
      return [
        { key: 'corretora', label: this.$i18n.t('spot.tableHeader.corretora'), tdClass: 'text-dark recentTransactionCell text-center', thClass: 'text-center'},
        { key: 'paridade', label: this.$i18n.t('spot.tableHeader.paridade'), tdClass: 'text-dark recentTransactionCell', },
        { key: 'totalCompra', label: this.$i18n.t('spot.tableHeader.totalCompra'), tdClass: 'text-dark recentTransactionCell', },
        { key: 'total', label: this.$i18n.t('spot.tableHeader.total'), tdClass: 'text-dark recentTransactionCell', },
        { key: 'pnl', label: this.$i18n.t('spot.tableHeader.pnl'), tdClass: 'text-dark recentTransactionCell text-center', thClass: 'text-center'} ,
        { key: 'dataCriacaoCompra', label: this.$i18n.t('spot.tableHeader.dataCriacaoCompra'), tdClass: 'text-dark recentTransactionCell text-center', thClass: 'text-center'},
        { key: 'duracao', label: this.$i18n.t('spot.tableHeader.duracao'), tdClass: 'text-dark recentTransactionCell'},
        { key: 'agente', label: this.$i18n.t('spot.tableHeader.trader'), tdClass: 'text-dark recentTransactionCell'},
      ]
    },

    carteiraAtualizacaoElapsed(){
      if(this.atualizacaoCarteira!=null) {
        return this.elapsedTime2(this.atualizacaoCarteira, this.dataAtual)
      }
      return null
    },

    isThemeDark() {
      return this.$store.state.appConfig.layout.skin === 'dark'
    },

    windowWidth() {
      return this.$store.state.app.windowWidth
    },

    carteiraLegenda() {
      const palette = ['#3b82f6', '#3f4249', '#2b2e33', '#1C1C1E', '#121214', '#3f4249']
      const total = this.seriesCarteira.reduce((acc, v) => acc + Number(v || 0), 0)
      return this.labelsCarteira.map((label, i) => {
        const usd = Number(this.seriesCarteira[i] || 0)
        return {
          label: label === 'Outros' ? this.$t('spot.assets.others') : label,
          color: palette[i % palette.length],
          usd,
          pct: total > 0 ? (usd / total) * 100 : 0,
        }
      })
    },

    carteiraGrafico() {
      return {
        chart: {
          type: 'donut',
          height: 240,
          fontFamily: 'Geist, system-ui, sans-serif',
        },
        colors: ['#3b82f6', '#3f4249', '#2b2e33', '#1C1C1E', '#121214', '#3f4249'],
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
          formatter(val, opts) {
            const value = opts.w.globals.seriesTotals[opts.seriesIndex]
            return `${opts.w.config.labels[opts.seriesIndex]}`
          },
          style: {
            fontSize: '16px',
            fontWeight: '600',
            colors: this.isThemeDark ? ['#FFFFFF'] : ['#1F2937'],
            textShadow: this.isThemeDark ? 'none' : '0 1px 2px rgba(0,0,0,0.1)'
          },
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 1,
            color: this.isThemeDark ? '#000000' : '#FFFFFF',
            opacity: this.isThemeDark ? 0.8 : 0.9
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 500,
                  color: this.isThemeDark ? '#8b8f98' : '#6B7280',
                  offsetY: -5
                },
                value: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: "'Geist Mono', 'SFMono-Regular', Menlo, monospace",
                  color: this.isThemeDark ? '#ffffff' : '#4B5563',
                  offsetY: 8,
                  formatter: (val) => `$${parseFloat(val).toFixed(2)}`
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: 'Total',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: this.isThemeDark ? '#8b8f98' : '#6B7280',
                  formatter: (w) => {
                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                    return `$${parseFloat(total).toFixed(2)}`
                  }
                }
              }
            }
          }
        },
        stroke: {
          show: true,
          width: 3,
          colors: this.isThemeDark ? ['#0F0F11'] : ['#FFFFFF']
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.15
            }
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.7
            }
          }
        },
        tooltip: {
          enabled: true,
          theme: this.isThemeDark ? 'dark' : 'light',
          style: {
            fontSize: '14px',
            fontFamily: 'Geist, system-ui, sans-serif'
          },
          y: {
            formatter: (val) => `$${parseFloat(val).toFixed(2)}`
          }
        },
        labels: this.labelsCarteira
      }
    },

    outrosValor() {
      const idx = this.labelsCarteira.findIndex(label => label === 'Outros')
      const outrosTotal = idx >= 0 ? this.seriesCarteira[idx] : 0
      return parseFloat(outrosTotal).toFixed(2)
    },

    exchangeConfigWithTranslations() {
      return this.availableExchanges
    },

    currentExchangeConfig() {
      if (this.selectedExchange && this.exchangeConfigWithTranslations[this.selectedExchange]) {
        return this.exchangeConfigWithTranslations[this.selectedExchange]
      }
      // Fallback para primeira exchange disponível ou binance
      const firstExchange = Object.keys(this.availableExchanges)[0]
      return this.availableExchanges[firstExchange] || {
        name: 'Binance',
        variant: 'light-warning',
        cardClass: 'binance-theme',
        color: '#F0B90B',
      }
    },
  },

  watch: {
    selectedExchange(newVal, oldVal) {
      if (newVal && oldVal != null && newVal !== oldVal) {
        this.atualizarDados()
      }
    },
  },

  created() {
    if (!store.hasModule(USUARIO_SPOT_STORE_MODULE_NAME)) store.registerModule(USUARIO_SPOT_STORE_MODULE_NAME, usuarioStoreModule)
    this.isVerificaAssinaturaAtiva()
    if (!store.hasModule(SINAL_SPOT_STORE_MODULE_NAME)) store.registerModule(SINAL_SPOT_STORE_MODULE_NAME, sinalStoreModule)
    if (!store.hasModule(RELATORIO_FECHAMENTO_SPOT_STORE_MODULE_NAME)) store.registerModule(RELATORIO_FECHAMENTO_SPOT_STORE_MODULE_NAME, fechamentoStoreModule)
    
    // Definir a primeira corretora ativa como selecionada (com persistência)
    try {
      const persisted = JSON.parse(localStorage.getItem('userData') || '{}').selectedExchange
      this.selectedExchange = this.availableExchanges[persisted] ? persisted : this.firstActiveExchange
    } catch (e) {
      this.selectedExchange = this.firstActiveExchange
    }
    
    // Listener para atualizações do userData
    this.$root.$on('userDataUpdated', () => {
      this.$forceUpdate()
      // Verificar se precisa trocar a exchange selecionada
      if (this.firstActiveExchange !== this.selectedExchange) {
        this.selectedExchange = this.firstActiveExchange
      }
    })

    // Troca de corretora disparada pelo seletor do navbar (redesign Soulf):
    // apenas seta selectedExchange -> o watcher existente chama atualizarDados (cache=false)
    this.$root.$on('sf-exchange-changed', key => {
      if (this.availableExchanges[key] && key !== this.selectedExchange) {
        this.selectedExchange = key
      }
    })

    this.pollDataAtual();
    this.getCarteira(true)
    this.getVendaResumo()
    this.getUltimasVendas()
  },

  beforeDestroy() {
    if (store.hasModule(SINAL_SPOT_STORE_MODULE_NAME)) store.unregisterModule(SINAL_SPOT_STORE_MODULE_NAME)
    if (store.hasModule(RELATORIO_FECHAMENTO_SPOT_STORE_MODULE_NAME)) store.unregisterModule(RELATORIO_FECHAMENTO_SPOT_STORE_MODULE_NAME)
    if (store.hasModule(USUARIO_SPOT_STORE_MODULE_NAME)) store.unregisterModule(USUARIO_SPOT_STORE_MODULE_NAME)
    
    // Remover listeners
    this.$root.$off('userDataUpdated')
    this.$root.$off('sf-exchange-changed')
  },

  methods: {

    atualizarDados() {
      this.getCarteira(false)
      this.getUltimasVendas()
      this.getVendaResumo()
    },
    
    isVerificaAssinaturaAtiva() {
      store
        .dispatch('usuario-spot/isVerificaAssinaturaAtiva')
        .then(response => {
          if(response.data != true) {
            this.$router.push({ name: 'conta-assinatura' })
          }
        })
    },

    getUltimasVendas() {
      store
        .dispatch('fechamento-relatorio-spot/getUltimasVendas')
        .then(response => {
          this.dataListUltimasVendas = response.data.content
        })
    },

    getVendaResumo() {
      this.loadingOverlaySinal = true

      const currentDate = new Date()
      const primeiroDiaMes = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      const ultimoDiaMes = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

      const dataInicio = toIsoStringTimeZone(primeiroDiaMes)
      const dataFim = toIsoStringTimeZone(ultimoDiaMes)

      store
        .dispatch('sinal-relatorio-spot/getSinaisResumo', {
          dataInicio,
          dataFim,
        })
        .then(response => {
          this.seriesSinal = [response.data.totalAtivoPercent, response.data.totalGanhoPercent, response.data.totalPerdaPercent]
          this.resumoSinal = response.data
        }).finally(() => {
            this.loadingOverlaySinal = false
        });
    },

    async getCarteira(cache) {
      this.loadingOverlayCarteira = true

      const TOP_SYMBOLS = ['USDT', 'USDC', 'BTC', 'BNB', 'MX']
      const ensureNumber = v => (Number.isFinite(Number(v)) ? Number(v) : 0)
      const toSeriesValue = v => Number(ensureNumber(v).toFixed(6))

      const formatBalance = {
        USDT: { total: v => ensureNumber(v).toFixed(2), free: v => ensureNumber(v).toFixed(2) },
        USDC: { total: v => ensureNumber(v).toFixed(2), free: v => ensureNumber(v).toFixed(2) },
        BTC:  { total: v => ensureNumber(v),           free: v => v },           // mantém como veio
        BNB:  { total: v => ensureNumber(v),           free: v => ensureNumber(v) },
        MX:   { total: v => ensureNumber(v),           free: v => ensureNumber(v) },
      }

      try {
        const [walletRes, creditRes] = await Promise.allSettled([
          store.dispatch('usuario-spot/getCarteira', {
            cache,
            corretora: this.selectedExchange?.toUpperCase(),
          }),
          store.dispatch('usuario-spot/getCreditoDesempenho', cache),
        ])

        this.creditoDesempenho = creditRes.status === 'fulfilled' ? creditRes.value.data : null

        if (walletRes.status !== 'fulfilled') {
          this.errorLoadCarteira = walletRes.reason?.data || walletRes.reason?.message || 'Erro ao carregar carteira'
          return
        }

        this.errorLoadCarteira = null
        const { estimativaBTC, estimativaUSD, atualizacao, carteira = [] } = walletRes.value
        this.estimativaBTC = estimativaBTC
        this.estimativaUSD = estimativaUSD
        this.atualizacaoCarteira = atualizacao

        const bySymbol = carteira.reduce((acc, it) => {
          acc[it.simbolo] = {
            available: ensureNumber(it.disponivel),
            locked: ensureNumber(it.bloqueado),
            totalUSD: ensureNumber(it.totalUSD),
            rawAvailable: it.disponivel,
          }
          return acc
        }, {})

        const series = []
        const labels = []

        TOP_SYMBOLS.forEach(symbol => {
          const data = bySymbol[symbol]
          const available = data?.available ?? 0
          const locked = data?.locked ?? 0
          const totalUSD = data?.totalUSD ?? 0
          const f = formatBalance[symbol]
          const totalBalance = available + locked

          if (symbol === 'USDT') {
            this.totalUSDT = f.total(totalBalance)
            this.livreUSDT = f.free(available)
          } else if (symbol === 'USDC') {
            this.totalUSDC = f.total(totalBalance)
            this.livreUSDC = f.free(available)
          } else if (symbol === 'BTC') {
            this.totalBTC = f.total(totalBalance)
            this.livreBTC = f.free(data?.rawAvailable ?? 0)
          } else if (symbol === 'BNB') {
            this.totalBNB = f.total(totalBalance)
            this.livreBNB = f.free(available)
          } else if (symbol === 'MX') {
            this.totalMX = f.total(totalBalance)
            this.livreMX = f.free(available)
          }

          series.push(toSeriesValue(totalUSD))
          labels.push(symbol)
        })

        const othersUSD = carteira.reduce((sum, c) => {
          return TOP_SYMBOLS.includes(c.simbolo) ? sum : sum + ensureNumber(c.totalUSD)
        }, 0)

        if (othersUSD > 0) {
          series.push(othersUSD)
          labels.push('Outros')
        }

        this.seriesCarteira = series
        this.labelsCarteira = labels
      } finally {
        this.loadingOverlayCarteira = false
      }
    },

    pollDataAtual() {
      const self = this
      this.polling = setInterval(() => {
        self.dataAtual = new Date()
      }, 10000)
    },

    selectExchange(exchange) {
      // Verificar se a corretora está disponível/ativa
      if (!this.availableExchanges[exchange]) {
        return
      }
      
      this.selectedExchange = exchange
      try {
        const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
        currentUserData.selectedExchange = exchange
        localStorage.setItem('userData', JSON.stringify(currentUserData))
      } catch (e) { /* ignore */ }
      // Watcher cuidará de recarregar os dados quando a corretora mudar
    },

    // Método para obter parâmetros baseados na corretora selecionada
    getExchangeParams() {
      return {
        exchange: this.selectedExchange.toUpperCase()
      }
    },

    // (keyboard nav removed per request)

    // Lighten a HEX color by percentage (mix with white)
    lightenColor(hexColor, percent) {
      try {
        if (!hexColor) return '#ffffff'
        let hex = hexColor.toString().replace('#', '')
        if (hex.length === 3) {
          hex = hex.split('').map(ch => ch + ch).join('')
        }
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        const p = Math.max(0, Math.min(100, Number(percent))) / 100
        const rn = Math.round(r + (255 - r) * p)
        const gn = Math.round(g + (255 - g) * p)
        const bn = Math.round(b + (255 - b) * p)
        const toHex = v => v.toString(16).padStart(2, '0')
        return `#${toHex(rn)}${toHex(gn)}${toHex(bn)}`
      } catch (e) {
        return hexColor
      }
    },

  },
}
</script>

<style lang="scss" scoped>
/* ============================================================
   Soulf Home — spot (dark premium). Tokens vêm de
   src/assets/scss/soulf-theme.scss (:root --sf-*).
   ============================================================ */
.sf-home {
  padding: 28px 32px 60px;
  max-width: 1640px;
  width: 100%;

  @media (max-width: 1200px) { padding: 20px 16px 48px; }
  @media (max-width: 768px)  { padding: 16px 12px 40px; }
}

/* Faixa de mercado */
.sf-market {
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 16px;
  padding: 12px 16px;
  overflow: hidden;
}

/* Seletor de corretora */
.sf-exchange {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  .sf-exchange-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: var(--sf-card);
    border: 1px solid var(--sf-border);
    border-radius: 9px;
    color: var(--sf-fg-3);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color .15s ease, color .15s ease, background .15s ease;

    &:hover { border-color: var(--sf-border-strong); color: var(--sf-fg); }
    &.active {
      background: rgba(255,255,255,0.06);
      border-color: var(--sf-border-strong);
      color: #fff;
    }

    .sf-exchange-logo { width: 18px; height: 18px; object-fit: contain; }
  }
}

/* Grid principal de 2 colunas */
.sf-grid { display: grid; gap: 20px; }
.sf-grid-main {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
  @media (max-width: 992px) { grid-template-columns: 1fr; }
}

/* Card base */
.sf-card {
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 16px;
}
.sf-pad { padding: 24px; }
.sf-card-head { margin-bottom: 14px; }

/* Valor grande (métrica) */
.sf-value {
  display: flex;
  align-items: baseline;
  gap: 4px;

  .sf-value-cur { font-size: 15px; color: var(--sf-fg-3); }
  .sf-value-amt { font-size: 30px; font-weight: 500; color: var(--sf-fg); line-height: 1.1; }
}

/* Alocação (donut + legenda) — donut ~200px colado na legenda, sem sobra vertical */
.sf-alloc {
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 640px) { flex-direction: column; gap: 12px; }

  .sf-alloc-chart { flex: 0 0 200px; display: flex; justify-content: center; }
  .sf-alloc-legend { flex: 1; display: flex; flex-direction: column; gap: 7px; width: 100%; }
}

.sf-legend-row {
  display: flex;
  align-items: center;
  gap: 10px;

  .sf-legend-name { flex: 1; color: var(--sf-fg-2); font-size: 13px; }
  .sf-legend-val { color: var(--sf-fg-3); font-size: 13px; text-align: right; }
  .sf-legend-pct { width: 42px; text-align: right; color: var(--sf-fg-4); font-size: 12px; }
}

.sf-swatch {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
  display: inline-block;
}

/* Card de crédito */
.sf-credit {
  display: flex;
  flex-direction: column;

  .sf-credit-sub { font-size: 12px; color: var(--sf-fg-4); margin-top: 2px; }

  .sf-credit-actions {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 20px;
  }
}

.sf-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: transform .12s ease, background .12s ease;

  &:hover { transform: translateY(-1px); text-decoration: none; }

  &.sf-btn-primary { background: var(--sf-accent); color: var(--sf-accent-on); }
  &.sf-btn-secondary {
    background: var(--sf-code);
    border: 1px solid var(--sf-border);
    color: var(--sf-fg-2);
    &:hover { color: #fff; }
  }
}

/* Cabeçalho da página */
.sf-pagehead {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  .sf-pagetitle {
    font-size: 22px;
    font-weight: 600;
    color: var(--sf-fg);
    letter-spacing: -0.01em;
    margin: 4px 0 0;
  }
  .sf-pagehead-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.sf-live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: sfLive 2s infinite;
}
@keyframes sfLive {
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  70%  { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}

/* Indicador de atualização (chip no page-head) */
.sf-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 9px;
  color: var(--sf-fg-3);
  font-size: 12px;
  padding: 7px 12px;
  cursor: pointer;
  transition: color .15s ease, border-color .15s ease;
  &:hover { color: #fff; border-color: var(--sf-border-strong); }
}

/* Saldo de ativos (2 colunas como no protótipo) */
.sf-assets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}
.sf-asset {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--sf-card-2);
  border: 1px solid var(--sf-border);
  border-radius: 13px;
  padding: 16px 18px;

  .sf-asset-tile {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--sf-elev);
    border: 1px solid var(--sf-border);
    border-radius: 10px;
    font-family: 'Geist Mono','SFMono-Regular',Menlo,monospace;
    font-size: 12px;
    font-weight: 600;
  }
  .sf-asset-info { min-width: 0; }
  .sf-asset-total { font-size: 17px; font-weight: 500; color: var(--sf-fg); line-height: 1.2; }
  .sf-asset-free {
    font-size: 11px;
    color: var(--sf-fg-4);
    margin-top: 2px;
    span { color: var(--sf-fg-3); }
  }
}

/* Assertividade */
.sf-assert {
  display: flex;
  align-items: center;
  gap: 22px;

  @media (max-width: 640px) { flex-direction: column; }

  .sf-assert-chart { flex: 1 1 auto; min-width: 0; }
  .sf-assert-stats {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
.sf-stat {
  display: flex;
  align-items: center;
  gap: 10px;

  .sf-stat-label { color: var(--sf-fg-2); font-size: 13px; }
  .sf-stat-val { margin-left: auto; color: var(--sf-fg); font-size: 14px; }
}

/* Card de tabela */
.sf-table-card { overflow: hidden; }
.sf-table-title {
  padding: 18px 24px;
  border-bottom: 1px solid var(--sf-border);
  font-size: 15px;
  font-weight: 600;
  color: var(--sf-fg);
}
.sf-scroll { overflow-x: auto; }
.sf-muted { color: var(--sf-fg-4); }

/* Células */
.sf-cell-exchange {
  display: flex;
  align-items: center;
  gap: 8px;
  .sf-cell-exchange-logo { width: 18px; height: 18px; object-fit: contain; }
}
.sf-cell-pair {
  display: flex;
  align-items: center;
  gap: 8px;
  .sf-pair-base { font-weight: 500; color: var(--sf-fg); }
  .sf-pair-quote { color: var(--sf-fg-4); }
}
.sf-cell-pnl {
  small { display: block; opacity: .8; }
}
.sf-cell-date {
  display: flex;
  flex-direction: column;
  span:last-child { font-size: 12px; }
}
.sf-cell-agent {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sf-fg-3);
}
.sf-signal-pill {
  font-size: 10px;
  color: var(--sf-accent);
  background: rgba(255,255,255,0.05);
  border-radius: 5px;
  padding: 2px 6px;
}

/* Estado de erro */
.sf-errorstate {
  text-align: center;
  padding: 32px 12px;
  .feather { color: var(--sf-red); margin-bottom: 12px; }
  h4 { color: var(--sf-fg); margin-bottom: 6px; }
  p { color: var(--sf-fg-4); margin-bottom: 16px; }
}
</style>

<style lang="scss">
/* Ajustes finos na b-table dentro do card Soulf (não-scoped p/ atingir a tabela do BootstrapVue) */
.sf-table.table { margin-bottom: 0; }
.sf-table.table thead th:first-child,
.sf-table.table tbody td:first-child { padding-left: 24px; }
.sf-table.table thead th:last-child,
.sf-table.table tbody td:last-child { padding-right: 24px; }
</style>
